/**
 * Read what the pipeline's export filenames already encode.
 *
 * A dropped file is named like:
 *
 *     lambda-20260714-194920-c3177c97_2026-07-15.json
 *            \______/ \____/ \______/ \________/
 *              date    time   run id   export date
 *
 * Two of the real exports in this repository carry no root `Refreshed` or
 * `Created` key at all — their dates have to be read from the first record —
 * so the filename is often the cheapest honest evidence of which run came
 * first, and the only evidence available before either file is parsed.
 *
 * Pure and strict. A name that does not match this exact shape returns null
 * rather than a best guess: the caller then falls back to `assessFileOrder`,
 * which reads dates out of the files themselves. Guessing an order from a
 * name that merely looks similar would silently swap a reference and a
 * candidate, which inverts every finding in the run.
 *
 * **No timezone is claimed.** The filename states none, so this module does not
 * invent one (AGENTS.md rule 1). `startedAt` is a fixed-width local-looking
 * timestamp used only to compare two names from the same pipeline against each
 * other, never to place a run on a real clock.
 */

const RUN_FILENAME =
  /^lambda-(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})-([0-9a-f]+)_(\d{4}-\d{2}-\d{2})\.json$/i;

export type RunFilename = {
  fileName: string;
  /**
   * `YYYY-MM-DDTHH:MM:SS`, with no zone designator, because the name carries
   * none. Fixed width, so lexicographic order is chronological order.
   */
  startedAt: string;
  /** The opaque per-run segment. Distinguishes two runs started in one second. */
  runId: string;
  /** The `YYYY-MM-DD` after the underscore: the export date the pipeline stamped. */
  exportDate: string;
};

/** Parse an export filename, or null when it is not one. */
export function parseRunFilename(fileName: string): RunFilename | null {
  const match = RUN_FILENAME.exec(fileName.trim());
  if (match === null) return null;
  const [, year, month, day, hour, minute, second, runId, exportDate] = match;

  // Reject impossible clock values rather than accepting a name that merely
  // has the right number of digits.
  const monthNumber = Number(month);
  const dayNumber = Number(day);
  if (monthNumber < 1 || monthNumber > 12 || dayNumber < 1 || dayNumber > 31) return null;
  if (Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59) return null;

  return {
    fileName,
    startedAt: `${year}-${month}-${day}T${hour}:${minute}:${second}`,
    runId: runId!.toLowerCase(),
    exportDate: exportDate!
  };
}

/** Chronological order, oldest first. Fixed-width timestamps compare as strings. */
export function compareRuns(left: RunFilename, right: RunFilename): number {
  if (left.startedAt !== right.startedAt) return left.startedAt.localeCompare(right.startedAt);
  // Same second: fall back to the run id so the order is at least stable.
  return left.runId.localeCompare(right.runId);
}

export type RunPair = { reference: RunFilename; candidate: RunFilename };

/**
 * Assign two parsed names to their roles: the older run is the reference, the
 * newer is the candidate.
 *
 * Returns null when the two cannot be ordered — the same run twice, or two runs
 * indistinguishable by both timestamp and id. Refusing is the point: a pair the
 * filenames cannot separate must fall through to the file contents, and from
 * there to the person, rather than being assigned a direction by chance.
 */
export function orderRunPair(left: RunFilename, right: RunFilename): RunPair | null {
  const order = compareRuns(left, right);
  if (order === 0) return null;
  return order < 0 ? { reference: left, candidate: right } : { reference: right, candidate: left };
}

/**
 * The pair to offer from a folder of runs: the newest, against the one before it.
 *
 * That is the comparison a person almost always wants — "what changed in the
 * run that just landed" — and it is offered rather than applied: the caller
 * still shows the list so a different pair can be chosen.
 *
 * Unparseable names are dropped, not ordered by their raw string. A folder
 * holding one run, or none, yields no pair.
 */
export function newestPair(fileNames: string[]): RunPair | null {
  const parsed = fileNames
    .map((name) => parseRunFilename(name))
    .filter((run): run is RunFilename => run !== null)
    .sort(compareRuns);
  if (parsed.length < 2) return null;
  return { reference: parsed[parsed.length - 2]!, candidate: parsed[parsed.length - 1]! };
}

/** Every parseable run in a folder, newest first, for the picker. */
export function listRuns(fileNames: string[]): RunFilename[] {
  return fileNames
    .map((name) => parseRunFilename(name))
    .filter((run): run is RunFilename => run !== null)
    .sort((left, right) => compareRuns(right, left));
}

/** `2026-07-14 19:49:20`, for showing a run in the picker. */
export function formatRunStart(run: RunFilename): string {
  return run.startedAt.replace("T", " ");
}
